/**
 * QuoteCatalogPanel.jsx — Iter 140.6
 *
 * FULL CRUD UI for the Quote Catalog product library. Mounted as the
 * "Catalog" tab inside Quotes.jsx, visible only to super_admin and
 * contracts_agent (the gating happens at the parent + the backend
 * also enforces the role list).
 *
 * Layout:
 *   - Toolbar (search + category filter + + New Product)
 *   - Table of products with inline edit / delete
 *   - Add/Edit drawer (right side) with all editable fields
 */
import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Edit3, Trash2, Search, X, Loader2, Save, FileText, Package } from "lucide-react";

import { API, useAuth } from "@/App";
import { QuoteRichTextField } from "@/components/quotes/QuoteRichTextField";

const MODEL_OPTIONS = [
  { value: "fixed",            label: "Fixed price" },
  { value: "percentage",       label: "Percentage of monthly collections" },
  { value: "per_patient",      label: "Per patient / per transaction" },
  { value: "setup_discount",   label: "Setup fee discount" },
  { value: "product_discount", label: "Product discount" },
];

const DEFAULT_CATEGORIES = [
  "Application / Software",
  "Ancillary Products",
  "Service",
  "Care Coordination Service",
  "Care Coordination Application",
  "Implementation",
  "Discounts",
];

const EMPTY_DRAFT = {
  name: "",
  description: "",
  price: 0,
  unit: "/mo",
  model: "fixed",
  category: "Ancillary Products",
  active: true,
};

export default function QuoteCatalogPanel() {
  const { token } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [showInactive, setShowInactive] = useState(false);
  const [drawer, setDrawer] = useState(null); // null | "new" | {id, ...}
  const [saving, setSaving] = useState(false);

  const auth = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  // ── Category default Terms & Conditions ──────────────────────────────
  const [catTermsMap, setCatTermsMap] = useState({});
  const [termsCat, setTermsCat] = useState("");
  const [termsHtml, setTermsHtml] = useState("");
  const [termsSaving, setTermsSaving] = useState(false);

  const fetchCategoryTerms = async () => {
    try {
      const r = await axios.get(`${API}/quote-catalog/category-terms`, { headers: auth });
      const map = {};
      (r.data?.items || []).forEach(x => { if (x.category) map[x.category] = x.terms_html || ""; });
      setCatTermsMap(map);
    } catch { /* non-fatal */ }
  };
  useEffect(() => { if (token) fetchCategoryTerms(); /* eslint-disable-line */ }, [token]);
  // Load the selected category's saved terms into the editor.
  useEffect(() => { setTermsHtml(termsCat ? (catTermsMap[termsCat] || "") : ""); }, [termsCat, catTermsMap]);

  const saveCategoryTerms = async () => {
    if (!termsCat) { toast.error("Pick a category first"); return; }
    setTermsSaving(true);
    try {
      await axios.put(`${API}/quote-catalog/category-terms`, { category: termsCat, terms_html: termsHtml || "" }, { headers: auth });
      toast.success(`Saved default terms for "${termsCat}"`);
      await fetchCategoryTerms();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save category terms");
    } finally {
      setTermsSaving(false);
    }
  };

  const fetchRows = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (categoryFilter) params.set("category", categoryFilter);
      if (showInactive) params.set("include_inactive", "true");
      const r = await axios.get(`${API}/quote-catalog/products?${params}`, { headers: auth });
      setRows(r.data?.items || []);
    } catch (e) {
      const msg = e?.response?.data?.detail || "Couldn't load catalog";
      toast.error(typeof msg === "string" ? msg : "Couldn't load catalog");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchRows(); /* eslint-disable-line */ }, [categoryFilter, showInactive]);

  const filteredRows = useMemo(() => {
    // Iter 171 — sort by canonical category order first, then name.
    const base = !search ? rows : rows.filter(r =>
      (r.name || "").toLowerCase().includes(search.toLowerCase()) ||
      (r.description || "").toLowerCase().includes(search.toLowerCase()) ||
      (r.category || "").toLowerCase().includes(search.toLowerCase())
    );
    const catRank = (c) => {
      const idx = DEFAULT_CATEGORIES.indexOf(c);
      return idx === -1 ? DEFAULT_CATEGORIES.length : idx;
    };
    return [...base].sort((a, b) => {
      const ra = catRank(a.category);
      const rb = catRank(b.category);
      if (ra !== rb) return ra - rb;
      return (a.name || "").localeCompare(b.name || "");
    });
  }, [rows, search]);

  const categories = useMemo(() => {
    // Iter 171 — canonical order from the EHI Product Catalog spec.
    // Unknown DB categories are appended alphabetically.
    const set = new Set(DEFAULT_CATEGORIES);
    rows.forEach(r => { if (r.category) set.add(r.category); });
    const known = DEFAULT_CATEGORIES.filter(c => set.has(c));
    const extras = Array.from(set).filter(c => !DEFAULT_CATEGORIES.includes(c)).sort();
    return [...known, ...extras];
  }, [rows]);

  const startCreate = () => setDrawer({ ...EMPTY_DRAFT, _mode: "new" });
  const startEdit = (row) => setDrawer({ ...row, _mode: "edit" });
  const closeDrawer = () => setDrawer(null);

  const saveDrawer = async () => {
    if (!drawer?.name?.trim()) {
      toast.error("Name is required");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: drawer.name.trim(),
        description: drawer.description || "",
        price: parseFloat(drawer.price) || 0,
        unit: drawer.unit || "",
        model: drawer.model || "fixed",
        category: drawer.category || "Add-ons",
        active: !!drawer.active,
      };
      if (drawer._mode === "new") {
        await axios.post(`${API}/quote-catalog/products`, payload, { headers: auth });
        toast.success(`Added "${payload.name}" to the catalog`);
      } else {
        await axios.patch(`${API}/quote-catalog/products/${drawer.id}`, payload, { headers: auth });
        toast.success(`Updated "${payload.name}"`);
      }
      setDrawer(null);
      await fetchRows();
    } catch (e) {
      const msg = e?.response?.data?.detail || "Couldn't save";
      toast.error(typeof msg === "string" ? msg : "Couldn't save");
    } finally {
      setSaving(false);
    }
  };

  const deleteRow = async (row) => {
    if (!window.confirm(`Delete "${row.name}" from the catalog? This is a soft delete — the row is preserved for audit.`)) return;
    try {
      await axios.delete(`${API}/quote-catalog/products/${row.id}`, { headers: auth });
      toast.success(`Removed "${row.name}"`);
      await fetchRows();
    } catch (e) {
      toast.error("Couldn't delete");
    }
  };

  return (
    <div data-testid="quote-catalog-panel" style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      {/* Category default Terms & Conditions */}
      <div data-testid="category-terms-section" style={{ padding: "14px 16px", border: "0.5px solid var(--border)", borderRadius: 10, background: "var(--page-bg, #f8fafc)" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
          <FileText style={{ width: 14, height: 14, color: "var(--text-secondary)" }} />
          <div style={{ fontSize: 12, fontWeight: 700, color: "var(--text-primary)" }}>Category Default Terms &amp; Conditions</div>
        </div>
        <div style={{ fontSize: 11, color: "var(--text-tertiary)", marginBottom: 10 }}>
          These terms automatically appear when products from this category are added to a quote. Reps can edit them before finalizing.
        </div>
        <select
          data-testid="category-terms-select"
          value={termsCat}
          onChange={e => setTermsCat(e.target.value)}
          style={{ padding: "7px 10px", border: "0.5px solid var(--border)", borderRadius: 8, fontSize: 12, marginBottom: 8, minWidth: 260 }}
        >
          <option value="">Select a category…</option>
          {categories.map(c => <option key={c} value={c}>{c}{(catTermsMap[c] || "").trim() ? "  ✓" : ""}</option>)}
        </select>
        {termsCat && (
          <div data-testid="category-terms-editor">
            <QuoteRichTextField value={termsHtml} onChange={setTermsHtml} testid="category-terms" minHeight={160}
              placeholder="Enter default terms and conditions for this category…" />
            <button
              type="button"
              data-testid="save-category-terms-btn"
              onClick={saveCategoryTerms}
              disabled={termsSaving}
              className="btn btn-primary"
              style={{ marginTop: 10, fontSize: 12 }}
            >
              {termsSaving ? <Loader2 style={{ width: 13, height: 13 }} className="spin" /> : <Save style={{ width: 13, height: 13 }} />}
              {termsSaving ? "Saving…" : "Save Category Terms"}
            </button>
          </div>
        )}
      </div>
      <div data-testid="product-catalog-section" style={{ background: "var(--surface)", border: "0.5px solid var(--border)", borderRadius: 10, overflow: "hidden" }}>
      <div style={{ padding: "14px 16px 10px", borderBottom: "0.5px solid var(--border)", background: "var(--page-bg, #f8fafc)" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
          <Package style={{ width: 14, height: 14, color: "var(--text-secondary)" }} />
          <div data-testid="product-catalog-heading" style={{ fontSize: 12, fontWeight: 700, color: "var(--text-primary)" }}>Product Catalog</div>
        </div>
        <div style={{ fontSize: 11, color: "var(--text-tertiary)" }}>
          Products, pricing, and categories available to reps in the Quote Builder.
        </div>
      </div>
      {/* Toolbar */}
      <div style={{ padding: "12px 16px", borderBottom: "0.5px solid var(--border)", display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
        <div style={{ position: "relative", flex: "0 0 240px" }}>
          <Search style={{ position: "absolute", left: 8, top: "50%", transform: "translateY(-50%)", width: 13, height: 13, color: "var(--text-tertiary)" }} />
          <input
            data-testid="catalog-search"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search products…"
            style={{ width: "100%", paddingLeft: 28, padding: "7px 10px 7px 28px", border: "0.5px solid var(--border)", borderRadius: 8, fontSize: 12, fontFamily: "var(--font)" }}
          />
        </div>
        <select
          data-testid="catalog-category-filter"
          value={categoryFilter}
          onChange={e => setCategoryFilter(e.target.value)}
          style={{ padding: "7px 10px", border: "0.5px solid var(--border)", borderRadius: 8, fontSize: 12 }}
        >
          <option value="">All categories</option>
          {categories.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        <label style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 12, color: "var(--text-secondary)", cursor: "pointer" }}>
          <input type="checkbox" data-testid="catalog-show-inactive" checked={showInactive} onChange={e => setShowInactive(e.target.checked)} />
          Show inactive
        </label>
        <div style={{ flex: 1 }} />
        <button data-testid="catalog-new-btn" onClick={startCreate} className="btn btn-primary" style={{ fontSize: 12 }}>
          <Plus style={{ width: 14, height: 14 }} /> New Product
        </button>
      </div>

      {/* Table */}
      {loading ? (
        <div style={{ padding: 40, textAlign: "center", color: "var(--text-tertiary)" }}>
          <Loader2 style={{ width: 18, height: 18, color: "var(--blue)" }} className="spin" />
        </div>
      ) : filteredRows.length === 0 ? (
        <div style={{ padding: 48, textAlign: "center", color: "var(--text-tertiary)" }}>
          <div style={{ fontSize: 13, fontWeight: 500 }}>No products yet</div>
          <div style={{ fontSize: 11, marginTop: 4 }}>Click "+ New Product" to add the first catalog entry.</div>
        </div>
      ) : (
        <div>
          <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr 110px 150px 110px 90px", columnGap: 20, padding: "10px 16px", borderBottom: "0.5px solid var(--border)", fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.3, color: "var(--text-tertiary)" }}>
            <span>Product</span><span>Category</span><span style={{ textAlign: "right", paddingRight: 12 }}>Price</span><span style={{ paddingLeft: 12 }}>Pricing model</span><span>Unit</span><span></span>
          </div>
          {filteredRows.map(row => (
            <div
              key={row.id}
              data-testid={`catalog-row-${row.id}`}
              style={{ display: "grid", gridTemplateColumns: "2fr 1fr 110px 150px 110px 90px", columnGap: 20, padding: "10px 16px", borderBottom: "0.5px solid var(--border)", fontSize: 12, alignItems: "center", opacity: row.active ? 1 : 0.55 }}
            >
              <div>
                <div style={{ fontWeight: 600, color: "var(--text-primary)" }}>{row.name}</div>
                {row.description && <div style={{ fontSize: 10, color: "var(--text-tertiary)", marginTop: 2 }}>{row.description}</div>}
                {!row.active && <span style={{ fontSize: 9, padding: "1px 5px", borderRadius: 4, background: "var(--gray-light)", color: "var(--gray)", fontWeight: 700, textTransform: "uppercase", marginTop: 2, display: "inline-block" }}>Inactive</span>}
              </div>
              <div style={{ color: "var(--text-secondary)" }}>{row.category}</div>
              <div style={{ textAlign: "right", fontWeight: 600, paddingRight: 12 }}>${(row.price || 0).toLocaleString()}</div>
              <div style={{ color: "var(--text-tertiary)", fontSize: 11, paddingLeft: 12 }}>{MODEL_OPTIONS.find(o => o.value === row.model)?.label || row.model}</div>
              <div style={{ color: "var(--text-tertiary)", fontSize: 11 }}>{row.unit || "—"}</div>
              <div style={{ display: "flex", gap: 4, justifyContent: "flex-end" }}>
                <button data-testid={`catalog-edit-${row.id}`} onClick={() => startEdit(row)} style={{ background: "none", border: "0.5px solid var(--border)", borderRadius: 6, padding: "4px 6px", cursor: "pointer" }} title="Edit">
                  <Edit3 style={{ width: 12, height: 12, color: "var(--text-secondary)" }} />
                </button>
                <button data-testid={`catalog-delete-${row.id}`} onClick={() => deleteRow(row)} style={{ background: "none", border: "0.5px solid var(--red-light)", borderRadius: 6, padding: "4px 6px", cursor: "pointer" }} title="Delete">
                  <Trash2 style={{ width: 12, height: 12, color: "var(--red)" }} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
      </div>

      {/* Drawer */}
      {drawer && (
        <div data-testid="catalog-drawer" style={{ position: "fixed", inset: 0, background: "rgba(10,37,64,0.32)", zIndex: 9700, display: "flex", justifyContent: "flex-end" }} onClick={(e) => e.target === e.currentTarget && closeDrawer()}>
          <div style={{ width: "min(440px, 92vw)", height: "100%", background: "var(--surface)", padding: 22, overflowY: "auto", boxShadow: "-12px 0 30px rgba(0,0,0,0.2)" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
              <h3 style={{ fontSize: 15, fontWeight: 700, margin: 0 }}>{drawer._mode === "new" ? "Add new product" : "Edit product"}</h3>
              <button onClick={closeDrawer} style={{ background: "none", border: 0, cursor: "pointer", color: "var(--text-tertiary)" }}><X style={{ width: 16, height: 16 }} /></button>
            </div>
            <CatalogField label="Name (required)">
              <input data-testid="drawer-name" value={drawer.name} onChange={e => setDrawer({ ...drawer, name: e.target.value })} className="input" style={{ fontSize: 12 }} />
            </CatalogField>
            <CatalogField label="Description">
              <textarea data-testid="drawer-description" value={drawer.description || ""} onChange={e => setDrawer({ ...drawer, description: e.target.value })} rows={3} className="input" style={{ fontSize: 12, fontFamily: "inherit", resize: "vertical" }} />
            </CatalogField>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
              <CatalogField label="Price">
                <input data-testid="drawer-price" type="number" value={drawer.price ?? 0} onChange={e => setDrawer({ ...drawer, price: e.target.value })} className="input" style={{ fontSize: 12 }} step="0.01" />
              </CatalogField>
              <CatalogField label="Unit">
                <input data-testid="drawer-unit" value={drawer.unit || ""} onChange={e => setDrawer({ ...drawer, unit: e.target.value })} placeholder='e.g. "/mo", "one-time"' className="input" style={{ fontSize: 12 }} />
              </CatalogField>
            </div>
            <CatalogField label="Pricing model">
              <select data-testid="drawer-model" value={drawer.model || "fixed"} onChange={e => setDrawer({ ...drawer, model: e.target.value })} className="input" style={{ fontSize: 12 }}>
                {MODEL_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </CatalogField>
            <CatalogField label="Category">
              <input data-testid="drawer-category" value={drawer.category || ""} onChange={e => setDrawer({ ...drawer, category: e.target.value })} list="catalog-categories" className="input" style={{ fontSize: 12 }} />
              <datalist id="catalog-categories">
                {categories.map(c => <option key={c} value={c} />)}
              </datalist>
            </CatalogField>
            <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, marginTop: 10 }}>
              <input type="checkbox" data-testid="drawer-active" checked={!!drawer.active} onChange={e => setDrawer({ ...drawer, active: e.target.checked })} />
              Active (visible in QuoteBuilder)
            </label>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 22 }}>
              <button onClick={closeDrawer} className="btn" style={{ fontSize: 12 }}>Cancel</button>
              <button onClick={saveDrawer} disabled={saving} className="btn btn-primary" data-testid="drawer-save" style={{ fontSize: 12 }}>
                {saving ? <><Loader2 style={{ width: 12, height: 12 }} className="spin" /> Saving…</> : <><Save style={{ width: 12, height: 12 }} /> {drawer._mode === "new" ? "Add to catalog" : "Save changes"}</>}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function CatalogField({ label, children }) {
  return (
    <div style={{ marginBottom: 10 }}>
      <div style={{ fontSize: 11, color: "var(--text-secondary)", fontWeight: 600, marginBottom: 4 }}>{label}</div>
      {children}
    </div>
  );
}
